import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Settings, FileText, Clock, MessageSquare, Plus, Edit, Trash2, Send } from 'lucide-react';
import { InlineTemplateEditor } from './InlineTemplateEditor';
import { ClientTemplateEditor } from './ClientTemplateEditor';
import RuleEditor from './RuleEditor';

interface MessageTemplate {
  id: string;
  name: string;
  template_key: string;
  category: string;
  subject_template: string;
  html_template: string;
  text_template: string;
  sms_template?: string;
  variables: any; // Json from database
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface MessageRule {
  id: string;
  name: string;
  rule_key: string;
  tier: number;
  template_key?: string;
  trigger_conditions: any;
  timing_config: any;
  is_active: boolean;
  priority_order: number;
  created_at: string;
  updated_at: string;
}

interface ProviderConfig {
  id: string;
  name: string;
  provider_key: string;
  provider_type: string;
  config: any; // Json from database
  is_active: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface MessageCenterSettings {
  id: string;
  setting_key: string;
  setting_value: any;
  description?: string;
  updated_at: string;
}

const MessageCenterManagement = () => {
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [rules, setRules] = useState<MessageRule[]>([]);
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [settings, setSettings] = useState<MessageCenterSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [selectedRule, setSelectedRule] = useState<MessageRule | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ProviderConfig | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [editType, setEditType] = useState<'template' | 'rule' | null>(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load templates
      const { data: templatesData, error: templatesError } = await supabase
        .from('message_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (templatesError) throw templatesError;
      
      // Load rules
      const { data: rulesData, error: rulesError } = await supabase
        .from('message_rules')
        .select('*')
        .order('priority_order', { ascending: true });

      if (rulesError) throw rulesError;
      
      // Load providers
      const { data: providersData, error: providersError } = await supabase
        .from('message_provider_configs')
        .select('*')
        .order('created_at', { ascending: false });

      if (providersError) throw providersError;
      
      // Load settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('message_center_settings')
        .select('*')
        .order('setting_key', { ascending: true });

      if (settingsError) throw settingsError;

      setTemplates(templatesData || []);
      setRules(rulesData || []);
      setProviders(providersData || []);
      setSettings(settingsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load Message Center data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (template: Partial<MessageTemplate>) => {
    try {
      if (selectedTemplate?.id) {
        // Update existing template
        const { error } = await supabase
          .from('message_templates')
          .update(template)
          .eq('id', selectedTemplate.id);
        if (error) throw error;
        toast.success('Template updated successfully');
      } else {
        // Create new template
        const { error } = await supabase
          .from('message_templates')
          .insert([{
            name: template.name!,
            template_key: template.template_key!,
            category: template.category || 'notification',
            subject_template: template.subject_template!,
            text_template: template.text_template!,
            html_template: template.html_template,
            sms_template: template.sms_template,
            variables: template.variables || [],
            is_active: template.is_active ?? true
          }]);
        if (error) throw error;
        toast.success('Template created successfully');
      }
      loadData();
      setEditDialogOpen(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleSaveRule = async (rule: Partial<MessageRule>) => {
    try {
      if (selectedRule?.id) {
        // Update existing rule
        const { error } = await supabase
          .from('message_rules')
          .update(rule)
          .eq('id', selectedRule.id);
        if (error) throw error;
        toast.success('Rule updated successfully');
      } else {
        // Create new rule
        const { error } = await supabase
          .from('message_rules')
          .insert([{
            name: rule.name!,
            rule_key: rule.rule_key!,
            tier: rule.tier!,
            template_key: rule.template_key || null,
            trigger_conditions: rule.trigger_conditions || {},
            timing_config: rule.timing_config || {},
            is_active: rule.is_active ?? true,
            priority_order: rule.priority_order || 0
          }]);
        if (error) throw error;
        toast.success('Rule created successfully');
      }
      loadData();
      setEditDialogOpen(false);
      setSelectedRule(null);
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Failed to save rule');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('message_templates')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast.success('Template deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleDeleteRule = async (id: string) => {
    try {
      const { error } = await supabase
        .from('message_rules')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast.success('Rule deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleActive = async (type: 'template' | 'rule', id: string, isActive: boolean) => {
    try {
      const table = type === 'template' ? 'message_templates' : 'message_rules';
      const { error } = await supabase
        .from(table)
        .update({ is_active: isActive })
        .eq('id', id);
      if (error) throw error;
      toast.success(`${type} ${isActive ? 'activated' : 'deactivated'} successfully`);
      loadData();
    } catch (error) {
      console.error(`Error toggling ${type}:`, error);
      toast.error(`Failed to toggle ${type}`);
    }
  };

  const handleSaveProvider = async (provider: Partial<ProviderConfig>) => {
    try {
      if (selectedProvider?.id) {
        // Update existing provider
        const { error } = await supabase
          .from('message_provider_configs')
          .update(provider)
          .eq('id', selectedProvider.id);
        if (error) throw error;
        toast.success('Provider updated successfully');
      } else {
        // Create new provider
        const { error } = await supabase
          .from('message_provider_configs')
          .insert([{
            name: provider.name!,
            provider_key: provider.provider_key!,
            provider_type: provider.provider_type || 'email',
            config: provider.config || {},
            is_active: provider.is_active ?? true,
            is_default: provider.is_default ?? false
          }]);
        if (error) throw error;
        toast.success('Provider created successfully');
      }
      loadData();
      setProviderDialogOpen(false);
      setSelectedProvider(null);
    } catch (error) {
      console.error('Error saving provider:', error);
      toast.error('Failed to save provider');
    }
  };

  const handleDeleteProvider = async (id: string) => {
    try {
      const { error } = await supabase
        .from('message_provider_configs')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast.success('Provider deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast.error('Failed to delete provider');
    }
  };

  const handleSetDefaultProvider = async (id: string) => {
    try {
      // First, remove default from all providers
      await supabase
        .from('message_provider_configs')
        .update({ is_default: false });
      
      // Then set the selected provider as default
      const { error } = await supabase
        .from('message_provider_configs')
        .update({ is_default: true })
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Default provider updated successfully');
      loadData();
    } catch (error) {
      console.error('Error setting default provider:', error);
      toast.error('Failed to set default provider');
    }
  };

  const handleToggleProviderActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('message_provider_configs')
        .update({ is_active: isActive })
        .eq('id', id);
      if (error) throw error;
      toast.success(`Provider ${isActive ? 'activated' : 'deactivated'} successfully`);
      loadData();
    } catch (error) {
      console.error('Error toggling provider:', error);
      toast.error('Failed to toggle provider');
    }
  };

  const handleSendTestEmail = async (providerId: string) => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('test-email-provider', {
        body: {
          providerId,
          testEmail,
          subject: 'Test Email from Message Center',
          body: '<h1>Test Email</h1><p>This is a test email to verify your provider configuration is working correctly.</p>'
        }
      });

      if (error) throw error;
      
      if (data?.success) {
        toast.success(`Test email sent successfully via ${data.provider || 'provider'}`);
        setTestEmailDialogOpen(false);
        setTestEmail('');
      } else {
        throw new Error(data?.error || 'Unknown error occurred');
      }
    } catch (error: any) {
      console.error('Error sending test email:', error);
      // Show the actual provider error message instead of generic message
      const errorMessage = error?.message || error?.error || 'Failed to send test email';
      toast.error(`Test email failed: ${errorMessage}`);
    }
  };

  const handleUpdateBatchSize = async (newValue: number) => {
    try {
      const { error } = await supabase
        .from('message_center_settings')
        .update({ setting_value: newValue })
        .eq('setting_key', 'notification_batch_size');
      
      if (error) throw error;
      
      toast.success(`Batch size updated to ${newValue}`);
      loadData(); // Reload to show updated value
    } catch (error) {
      console.error('Error updating batch size:', error);
      toast.error('Failed to update batch size');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="templates" className="w-full">
        <TabsList className="w-full h-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <TabsTrigger value="templates" className="w-full flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Admin Templates
          </TabsTrigger>
          <TabsTrigger value="client-templates" className="w-full flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Client Templates
          </TabsTrigger>
          <TabsTrigger value="rules" className="w-full flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Rules
          </TabsTrigger>
          <TabsTrigger value="providers" className="w-full flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Providers
          </TabsTrigger>
          <TabsTrigger value="settings" className="w-full flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="broadcast" className="w-full flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Broadcast
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Templates</CardTitle>
                  <CardDescription>
                    Manage email templates for different notification tiers and broadcasts
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => {
                    setSelectedTemplate(null);
                    setEditType('template');
                    setEditDialogOpen(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            {template.template_key.replace('_', ' ').toUpperCase()}
                          </Badge>
                          {template.sms_template && (
                            <Badge variant="secondary" className="text-xs">
                              SMS
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{template.subject_template}</TableCell>
                      <TableCell>
                        <Switch
                          checked={template.is_active}
                          onCheckedChange={(checked) => handleToggleActive('template', template.id, checked)}
                        />
                      </TableCell>
                      <TableCell>{new Date(template.updated_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedTemplate(template);
                              setEditType('template');
                              setEditDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="client-templates" className="space-y-4">
          <ClientTemplateEditor />
        </TabsContent>

        <TabsContent value="rules" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Notification Rules</CardTitle>
                  <CardDescription>
                    Configure timing and trigger conditions for different notification tiers
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => {
                    setSelectedRule(null);
                    setEditType('rule');
                    setEditDialogOpen(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New Rule
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                     <TableHead>Name</TableHead>
                     <TableHead>Tier</TableHead>
                     <TableHead>Template</TableHead>
                     <TableHead>Timing</TableHead>
                     <TableHead>Status</TableHead>
                     <TableHead>Updated</TableHead>
                     <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                   {rules.map((rule) => {
                     const template = templates.find(t => t.template_key === rule.template_key);
                     const defaultTemplate = rule.tier === 1 ? 'Immediate' : 'Daily';
                     return (
                       <TableRow key={rule.id}>
                         <TableCell className="font-medium">{rule.name}</TableCell>
                         <TableCell>
                           <Badge variant={rule.tier === 1 ? 'destructive' : rule.tier === 2 ? 'default' : 'secondary'}>
                             Tier {rule.tier}
                           </Badge>
                         </TableCell>
                         <TableCell>
                           <div className="text-sm">
                             {template ? (
                               <Badge variant="default" className="text-xs">
                                 {template.name}
                               </Badge>
                             ) : (
                               <Badge variant="outline" className="text-xs">
                                 {defaultTemplate} Default
                               </Badge>
                             )}
                           </div>
                         </TableCell>
                         <TableCell className="max-w-xs truncate">
                           {rule.timing_config?.type || 'Immediate'}
                         </TableCell>
                         <TableCell>
                           <Switch
                             checked={rule.is_active}
                             onCheckedChange={(checked) => handleToggleActive('rule', rule.id, checked)}
                           />
                         </TableCell>
                         <TableCell>{new Date(rule.updated_at).toLocaleDateString()}</TableCell>
                         <TableCell>
                           <div className="flex items-center gap-2">
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={() => {
                                 setSelectedRule(rule);
                                 setEditType('rule');
                                 setEditDialogOpen(true);
                               }}
                             >
                               <Edit className="h-4 w-4" />
                             </Button>
                             <Button
                               variant="ghost"
                               size="sm"
                               onClick={() => handleDeleteRule(rule.id)}
                             >
                               <Trash2 className="h-4 w-4" />
                             </Button>
                           </div>
                         </TableCell>
                       </TableRow>
                     );
                   })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="providers" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Providers</CardTitle>
                  <CardDescription>
                    Configure and manage email service providers
                  </CardDescription>
                </div>
                <Button 
                  onClick={() => {
                    setSelectedProvider(null);
                    setProviderDialogOpen(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New Provider
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Default</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providers.map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell className="font-medium">{provider.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {provider.provider_key.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={provider.is_active}
                          onCheckedChange={(checked) => handleToggleProviderActive(provider.id, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        {provider.is_default && <Badge variant="default">Default</Badge>}
                      </TableCell>
                      <TableCell>{new Date(provider.updated_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedProvider(provider);
                              setProviderDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedProvider(provider);
                              setTestEmailDialogOpen(true);
                            }}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          {!provider.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetDefaultProvider(provider.id)}
                            >
                              Set Default
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteProvider(provider.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Global Settings</CardTitle>
              <CardDescription>
                Configure system-wide message center settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Master Notifications Toggle */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Notifications Control</Label>
                    <p className="text-sm text-muted-foreground">
                      Master switch to enable/disable all notification processing
                    </p>
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium">Enable Notifications</Label>
                      <p className="text-xs text-muted-foreground">
                        Controls whether any notifications are processed and sent to users
                      </p>
                    </div>
                    <Switch
                      checked={(() => {
                        const notifSetting = settings.find(s => s.setting_key === 'notifications_enabled');
                        return notifSetting?.setting_value?.enabled ?? true;
                      })()}
                      onCheckedChange={async (checked) => {
                        try {
                          const { error } = await supabase
                            .from('message_center_settings')
                            .upsert({
                              setting_key: 'notifications_enabled',
                              setting_value: { enabled: checked },
                              description: 'Global toggle for all notification processing'
                            });
                          
                          if (error) throw error;
                          
                          toast.success(`Notifications ${checked ? 'enabled' : 'disabled'} globally`);
                          loadData();
                        } catch (error) {
                          console.error('Error updating notifications setting:', error);
                          toast.error('Failed to update notifications setting');
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Batch Processing Section */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Batch Processing</Label>
                    <p className="text-sm text-muted-foreground">
                      Control notification processing rate and performance
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="batch-size">Batch Size</Label>
                      <div className="flex gap-2">
                        <Input
                          id="batch-size"
                          type="number"
                          min="1"
                          max="500"
                          value={(() => {
                            const batchSetting = settings.find(s => s.setting_key === 'notification_batch_size');
                            return batchSetting?.setting_value || 50;
                          })()}
                          onChange={(e) => {
                            const newValue = Math.max(1, Math.min(500, parseInt(e.target.value) || 50));
                            handleUpdateBatchSize(newValue);
                          }}
                          placeholder="50"
                          className="max-w-24"
                        />
                        <Button
                          size="sm"
                          onClick={() => {
                            const input = document.getElementById('batch-size') as HTMLInputElement;
                            if (input) {
                              const newValue = Math.max(1, Math.min(500, parseInt(input.value) || 50));
                              handleUpdateBatchSize(newValue);
                            }
                          }}
                        >
                          Save
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Number of notifications processed every 2 minutes. Higher values process faster but use more resources.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Other Settings */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">System Settings</Label>
                    <p className="text-sm text-muted-foreground">
                      View and manage other system configurations
                    </p>
                  </div>
                  {settings.filter(s => s.setting_key !== 'notification_batch_size').map((setting) => (
                    <div key={setting.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <Label className="text-sm font-medium">{setting.setting_key}</Label>
                        {setting.description && (
                          <p className="text-sm text-muted-foreground">{setting.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{JSON.stringify(setting.setting_value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="broadcast" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Broadcast Messages</CardTitle>
              <CardDescription>
                Send platform-wide announcements to all users
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="broadcast-subject">Subject</Label>
                    <Input id="broadcast-subject" placeholder="Enter broadcast subject" />
                  </div>
                  <div>
                    <Label htmlFor="broadcast-template">Template</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.filter(t => t.category === 'broadcast').map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="broadcast-message">Message</Label>
                  <Textarea 
                    id="broadcast-message" 
                    placeholder="Enter your broadcast message"
                    rows={6}
                  />
                </div>
                <Button className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Send Broadcast
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {editType === 'template' 
                ? (selectedTemplate ? 'Edit Template' : 'Create Template')
                : (selectedRule ? 'Edit Rule' : 'Create Rule')
              }
            </DialogTitle>
            <DialogDescription>
              {editType === 'template' 
                ? 'Configure email template content and settings'
                : 'Set up notification timing and trigger conditions'
              }
            </DialogDescription>
          </DialogHeader>
          
          {editType === 'template' && (
          <InlineTemplateEditor 
            template={selectedTemplate} 
            onSave={handleSaveTemplate}
            onCancel={() => setEditDialogOpen(false)}
          />
          )}
          
          {editType === 'rule' && (
          <RuleEditor 
            rule={selectedRule} 
            templates={templates}
            onSave={handleSaveRule}
            onCancel={() => setEditDialogOpen(false)}
          />
          )}
        </DialogContent>
      </Dialog>

      {/* Provider Dialog */}
      <Dialog open={providerDialogOpen} onOpenChange={setProviderDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedProvider ? 'Edit Provider' : 'Create Provider'}
            </DialogTitle>
            <DialogDescription>
              Configure email service provider settings and credentials
            </DialogDescription>
          </DialogHeader>
          
          <ProviderEditor 
            provider={selectedProvider} 
            onSave={handleSaveProvider}
            onCancel={() => setProviderDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email to verify provider configuration for: {selectedProvider?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="test-email">Test Email Address</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter test email address"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => selectedProvider && handleSendTestEmail(selectedProvider.id)}>
              <Send className="h-4 w-4 mr-2" />
              Send Test Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Provider Editor Component
const ProviderEditor = ({ 
  provider, 
  onSave, 
  onCancel 
}: { 
  provider: ProviderConfig | null; 
  onSave: (provider: Partial<ProviderConfig>) => void;
  onCancel: () => void;
}) => {
  const [formData, setFormData] = useState({
    name: provider?.name || '',
    provider_key: provider?.provider_key || '',
    provider_type: provider?.provider_type || 'email',
    config: provider?.config || {},
    is_active: provider?.is_active ?? true,
    is_default: provider?.is_default ?? false
  });

  const [apiKeyStatus, setApiKeyStatus] = useState<{ [key: string]: boolean }>({});
  const [savingApiKey, setSavingApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');

  useEffect(() => {
    if (formData.provider_key) {
      checkApiKeyStatus();
    }
  }, [formData.provider_key]);

  const checkApiKeyStatus = async () => {
    if (!formData.provider_key) return;
    
    try {
      const { data, error } = await supabase.functions.invoke('manage-api-keys', {
        body: { action: 'get', filter_provider: formData.provider_key }
      });
      
      if (!error && data?.keys) {
        const hasKey = data.keys.some((key: any) => 
          key.provider === formData.provider_key && 
          key.key_name === getKeyNameForProvider(formData.provider_key)
        );
        setApiKeyStatus(prev => ({
          ...prev,
          [formData.provider_key]: hasKey
        }));
      }
    } catch (error) {
      console.error('Error checking API key status:', error);
    }
  };

  const getKeyNameForProvider = (provider: string) => {
    return `${provider}_api_key`;
  };

  const handleSaveApiKey = async () => {
    if (!apiKeyInput.trim() || !formData.provider_key) return;
    
    setSavingApiKey(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-api-keys', {
        body: {
          action: 'save',
          provider: formData.provider_key,
          key_name: getKeyNameForProvider(formData.provider_key),
          value: apiKeyInput.trim()
        }
      });
      
      if (error) throw error;
      
      if (data?.success) {
        toast.success(`API key saved successfully (ends with ...${data.maskedSuffix || '****'})`);
        setApiKeyInput('');
        checkApiKeyStatus();
      } else {
        toast.error(data?.error || 'Failed to save API key');
      }
    } catch (error) {
      console.error('Error saving API key:', error);
      toast.error('Failed to save API key');
    } finally {
      setSavingApiKey(false);
    }
  };

  const handleRemoveApiKey = async () => {
    if (!formData.provider_key) return;
    
    try {
      // First get the key ID to delete
      const { data: keysData } = await supabase.functions.invoke('manage-api-keys', {
        body: { action: 'get', filter_provider: formData.provider_key }
      });
      
      if (keysData?.keys?.length > 0) {
        const keyToDelete = keysData.keys.find((key: any) => 
          key.provider === formData.provider_key && 
          key.key_name === getKeyNameForProvider(formData.provider_key)
        );
        
        if (keyToDelete) {
          const { data, error } = await supabase.functions.invoke('manage-api-keys', {
            body: {
              action: 'delete',
              id: keyToDelete.id
            }
          });
          
          if (error) throw error;
          
          toast.success('API key removed successfully');
          checkApiKeyStatus();
        } else {
          toast.error('No matching API key found to remove');
        }
      } else {
        toast.error('No API key found to remove');
      }
    } catch (error) {
      console.error('Error removing API key:', error);
      toast.error('Failed to remove API key');
    }
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleConfigChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [key]: value
      }
    }));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="provider-name">Provider Name</Label>
          <Input
            id="provider-name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="My Email Provider"
          />
        </div>
        <div>
          <Label htmlFor="provider-key">Provider Type</Label>
          <Select value={formData.provider_key} onValueChange={(value) => setFormData(prev => ({ ...prev, provider_key: value }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="resend">Resend</SelectItem>
              <SelectItem value="sendgrid">SendGrid</SelectItem>
              <SelectItem value="smtp2go">SMTP2GO</SelectItem>
              <SelectItem value="mailgun">Mailgun</SelectItem>
              <SelectItem value="postmark">Postmark</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="from-email">From Email Address</Label>
        <Input
          id="from-email"
          type="email"
          value={formData.config.from || ''}
          onChange={(e) => handleConfigChange('from', e.target.value)}
          placeholder="noreply@yourdomain.com"
        />
      </div>

      <div>
        <Label htmlFor="from-name">From Name (Optional)</Label>
        <Input
          id="from-name"
          value={formData.config.fromName || ''}
          onChange={(e) => handleConfigChange('fromName', e.target.value)}
          placeholder="Your App Name"
        />
      </div>

      {formData.provider_key === 'sendgrid' && (
        <div>
          <Label htmlFor="sendgrid-template-id">SendGrid Template ID (Optional)</Label>
          <Input
            id="sendgrid-template-id"
            value={formData.config.templateId || ''}
            onChange={(e) => handleConfigChange('templateId', e.target.value)}
            placeholder="d-1234567890abcdef"
          />
        </div>
      )}

      {formData.provider_key === 'smtp2go' && (
        <div>
          <Label htmlFor="smtp2go-sender">SMTP2GO Sender (Optional)</Label>
          <Input
            id="smtp2go-sender"
            value={formData.config.sender || ''}
            onChange={(e) => handleConfigChange('sender', e.target.value)}
            placeholder="Custom sender identifier"
          />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Switch
            id="provider-active"
            checked={formData.is_active}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
          />
          <Label htmlFor="provider-active">Active</Label>
        </div>
        
        <div className="flex items-center space-x-2">
          <Switch
            id="provider-default"
            checked={formData.is_default}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked }))}
          />
          <Label htmlFor="provider-default">Set as Default</Label>
        </div>
      </div>

      {/* API Key Management Section */}
      {formData.provider_key && (
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">API Key Management</h4>
              <p className="text-sm text-muted-foreground">
                Manage your {formData.provider_key.toUpperCase()} API key for testing and email sending
              </p>
            </div>
            <div className="flex items-center gap-2">
              {apiKeyStatus[formData.provider_key] ? (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  API Key Configured
                </Badge>
              ) : (
                <Badge variant="outline" className="text-orange-600 border-orange-600">
                  No API Key
                </Badge>
              )}
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder={`Enter your ${formData.provider_key.toUpperCase()} API Key`}
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleSaveApiKey}
                disabled={!apiKeyInput.trim() || savingApiKey}
                size="sm"
              >
                {savingApiKey ? 'Saving...' : 'Save Key'}
              </Button>
            </div>
            
            {apiKeyStatus[formData.provider_key] && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  API key is securely stored and encrypted
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRemoveApiKey}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  Remove Key
                </Button>
              </div>
            )}
          </div>
          
          <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded">
            <strong>Note:</strong> API keys saved here are encrypted and used for all email sending. 
            No project-level secrets configuration needed - this UI handles everything!
          </div>
        </div>
      )}
      
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave}>Save Provider</Button>
      </DialogFooter>
    </div>
  );
};

export default MessageCenterManagement;